import React, { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast, { Toaster } from 'react-hot-toast'
import { FiChevronLeft, FiUploadCloud } from 'react-icons/fi'
import ComptoirService from '../../service/ComptoirService'
import '../styles/AdminForm.css'

const DIM_ROWS = [
  { size: '1', dimKey: 'dimension1', priceKey: 'prix1', required: true },
  { size: '2', dimKey: 'dimension2', priceKey: 'prix2', required: false },
  { size: '3', dimKey: 'dimension3', priceKey: 'prix3', required: false },
  { size: '4', dimKey: 'dimension4', priceKey: 'prix4', required: false },
  { size: '5', dimKey: 'dimension5', priceKey: 'prix5', required: false },
]

const FormComptoir = () => {
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const authorization = localStorage.getItem('token')
  const [nom] = useState('comptoir')

  const [form, setForm] = useState({
    titre: '', reference: '', disponibilite: '',
    description: '', description2: '',
    prix: '', prix1: '', prix2: '', prix3: '', prix4: '', prix5: '',
    dimension1: '', dimension2: '', dimension3: '', dimension4: '', dimension5: '',
  })

  const [image, setImage] = useState(null)
  const [preview, setPreview] = useState(null)
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((f) => ({ ...f, [name]: value }))
    if (errors[name]) setErrors((err) => ({ ...err, [name]: null }))
  }

  const handleFile = (fileList) => {
    const file = fileList?.[0]
    if (!file || !file.type.startsWith('image/')) return
    if (preview) URL.revokeObjectURL(preview)
    setImage(file)
    setPreview(URL.createObjectURL(file))
    if (errors.image) setErrors((err) => ({ ...err, image: null }))
  }

  const handleDrop = (e) => {
    e.preventDefault()
    handleFile(e.dataTransfer.files)
  }

  const removeImage = () => {
    if (preview) URL.revokeObjectURL(preview)
    setImage(null)
    setPreview(null)
  }

  const validate = () => {
    const err = {}
    if (!form.titre.trim()) err.titre = 'Le nom est requis'
    if (!form.reference.trim()) err.reference = 'La référence est requise'
    if (!form.disponibilite) err.disponibilite = 'Sélectionnez une disponibilité'
    if (!form.prix) err.prix = 'Le prix est requis'
    if (!form.dimension1.trim()) err.dimension1 = 'Dimension 1 requise'
    if (!form.prix1) err.prix1 = 'Prix 1 requis'
    if (!image) err.image = 'Ajoutez une image'
    setErrors(err)
    return Object.keys(err).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) {
      document.querySelector('.ata-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    const formData = new FormData()
    Object.entries(form).forEach(([key, val]) => formData.append(key, val))
    formData.append('image', image)
    formData.append('nom', nom)

    setSubmitting(true)
    try {
      await ComptoirService.ajouter(formData, authorization)
      toast.success('Comptoir ajouté avec succès')
      setTimeout(() => navigate('/admin/Comptoir'), 800)
    } catch (err) {
      toast.error("Échec de l'ajout")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="ata-page">
      <Toaster />

      <div className="ata-header">
        <button type="button" className="ata-back" onClick={() => navigate('/admin/Comptoir')}>
          <FiChevronLeft size={16} />
        </button>
        <div>
          <h1 className="ata-title">Ajouter un comptoir</h1>
          <p className="ata-subtitle">Renseignez les informations du nouveau produit</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="ata-grid">
          <div className="ata-col-left">
            <div className="ata-card">
              <div className="ata-card__label">Photo du comptoir</div>

              {!preview ? (
                <div
                  className={`ata-dropzone ${errors.image ? 'ata-dropzone--error' : ''}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current.click()}
                >
                  <FiUploadCloud size={32} />
                  <p className="ata-dropzone__text">Glisser-déposer ou <span>cliquer pour choisir</span></p>
                  <p className="ata-dropzone__hint">JPG, PNG</p>
                  <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={(e) => handleFile(e.target.files)} />
                </div>
              ) : (
                <div className="ata-preview-grid">
                  <div className="ata-preview-item">
                    <img src={preview} alt="preview" />
                    <span className="ata-preview-main">Principale</span>
                    <button type="button" className="ata-preview-remove" onClick={removeImage}>✕</button>
                  </div>
                </div>
              )}
              {errors.image && <p className="ata-error">{errors.image}</p>}
            </div>
          </div>

          <div className="ata-col-right">
            <div className="ata-card">
              <div className="ata-card__label">Informations générales</div>

              <div className="ata-field">
                <label>Nom *</label>
                <input type="text" name="titre" value={form.titre} onChange={handleChange} placeholder="Ex: Comptoir d'accueil 140x70" className={errors.titre ? 'ata-input--error' : ''} />
                {errors.titre && <p className="ata-error">{errors.titre}</p>}
              </div>

              <div className="ata-row">
                <div className="ata-field">
                  <label>Référence *</label>
                  <input type="text" name="reference" value={form.reference} onChange={handleChange} placeholder="REF-001" className={errors.reference ? 'ata-input--error' : ''} />
                  {errors.reference && <p className="ata-error">{errors.reference}</p>}
                </div>
                <div className="ata-field">
                  <label>Disponibilité *</label>
                  <select name="disponibilite" value={form.disponibilite} onChange={handleChange} className={errors.disponibilite ? 'ata-input--error' : ''}>
                    <option value="" hidden>Sélectionner</option>
                    <option value="En stock">En stock</option>
                    <option value="Hors stock">Hors stock</option>
                    <option value="Sur commande">Sur commande</option>
                    <option value="En arrivage">En arrivage</option>
                  </select>
                  {errors.disponibilite && <p className="ata-error">{errors.disponibilite}</p>}
                </div>
              </div>

              <div className="ata-row">
                <div className="ata-field">
                  <label>Prix de base *</label>
                  <input type="number" name="prix" value={form.prix} onChange={handleChange} placeholder="Prix" className={errors.prix ? 'ata-input--error' : ''} />
                  {errors.prix && <p className="ata-error">{errors.prix}</p>}
                </div>
              </div>

              <div className="ata-field">
                <label>Description</label>
                <textarea rows={4} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              </div>

              <div className="ata-field">
                <label>Description détaillée</label>
                <textarea rows={6} value={form.description2} onChange={(e) => setForm((f) => ({ ...f, description2: e.target.value }))} />
              </div>
            </div>

            <div className="ata-card">
              <div className="ata-card__label">Dimensions &amp; Tarifs</div>
              {DIM_ROWS.map(({ size, dimKey, priceKey, required }) => (
                <div key={size} className="ata-dim-row">
                  <span className="ata-dim-badge">{size}</span>
                  <div className="ata-field ata-field--inline">
                    <input type="text" name={dimKey} value={form[dimKey]} onChange={handleChange} placeholder={required ? 'Dimension' : 'Optionnel'} className={errors[dimKey] ? 'ata-input--error' : ''} />
                  </div>
                  <div className="ata-field ata-field--inline ata-field--price">
                    <input type="number" name={priceKey} value={form[priceKey]} onChange={handleChange} placeholder="Prix" className={errors[priceKey] ? 'ata-input--error' : ''} />
                    <span className="ata-currency">DT</span>
                  </div>
                </div>
              ))}
              {(errors.dimension1 || errors.prix1) && <p className="ata-error">La dimension 1 et son prix sont requis</p>}
            </div>
          </div>
        </div>

        <div className="ata-footer-actions">
          <button type="button" className="ata-btn ata-btn--ghost" onClick={() => navigate('/admin/Comptoir')}>Annuler</button>
          <button type="submit" className="ata-btn ata-btn--primary" disabled={submitting}>
            {submitting ? (<><span className="ata-spinner" /> Enregistrement…</>) : 'Ajouter le comptoir'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default FormComptoir
